export type LegacyEntityToolCall = Record<string, unknown> & {
  type?: string;
  toolInvoke: {
    toolCallId?: string;
    requiresAction?: boolean;
    toolResult?: unknown;
    actionType?: string;
  };
};

export type ParsedMessagePart =
  | { type: "text"; text: string }
  | { type: "tool-call"; toolCall: LegacyEntityToolCall };

const COMPLETE_PROHIBITED_BLOCK = /<(handoff|chains)>[\s\S]*?<\/\1>/gi;
const PROHIBITED_TAG_STEM = /<\/?(?:handoff|chains?|chain)\b/i;

/**
 * Removes retired prompt-authored handoff/reasoning protocol without projecting it into UI.
 * An unmatched prohibited tag fails closed by discarding the remainder of the message.
 */
function stripRetiredPromptProtocol(content: string): string {
  const withoutCompleteBlocks = content.replace(COMPLETE_PROHIBITED_BLOCK, "\n");
  const prohibitedStart = withoutCompleteBlocks.search(PROHIBITED_TAG_STEM);
  return prohibitedStart === -1
    ? withoutCompleteBlocks
    : withoutCompleteBlocks.slice(0, prohibitedStart);
}

/**
 * Parses historical messages that predate persisted rawMessage content.
 * Valid `<json>` entity collections remain supported; retired `<handoff>` and `<chains>`
 * content is suppressed rather than accepted as visible text or reasoning.
 */
export function parseMessageContent(content: string): ParsedMessagePart[] {
  const safeContent = stripRetiredPromptProtocol(content);
  const parts: ParsedMessagePart[] = [];
  const jsonBlock = /<json>([\s\S]+?)<\/json>/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = jsonBlock.exec(safeContent))) {
    if (match.index > lastIndex) {
      const text = safeContent.slice(lastIndex, match.index).trim();
      if (text) parts.push({ type: "text", text });
    }

    try {
      const parsed = JSON.parse(match[1]) as { entities?: unknown };
      if (Array.isArray(parsed.entities)) {
        for (const candidate of parsed.entities) {
          if (
            candidate &&
            typeof candidate === "object" &&
            "toolInvoke" in candidate &&
            candidate.toolInvoke &&
            typeof candidate.toolInvoke === "object"
          ) {
            parts.push({
              type: "tool-call",
              toolCall: candidate as LegacyEntityToolCall,
            });
          }
        }
      }
    } catch {
      // Protocol payloads are not user-visible prose. Invalid legacy JSON stays suppressed.
    }

    lastIndex = jsonBlock.lastIndex;
  }

  if (lastIndex < safeContent.length) {
    const text = safeContent.slice(lastIndex).trim();
    if (text) parts.push({ type: "text", text });
  }

  return parts;
}
